/**
 * Ginásios tipados (PLANO-DIFERENCIAIS Pilar 4 — PvE estruturado). Times fixos por Líder,
 * reusa o motor de combate existente. Núcleo de dados + regras puras, sem DOM/I/O.
 */

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

/** 8 ginásios, um por tipo/cidade, em ordem de progressão (badge anterior destrava o próximo). */
export const GYMS = Object.freeze([
  Object.freeze({
    id: 'boulder', order: 1, city: 'Pewter', type: 'pedra', leaderName: 'Brock',
    badgeId: 'boulder-badge', level: 14, recommendedPower: 140,
    team: Object.freeze([
      Object.freeze({ species: 'geodude', level: 12 }),
      Object.freeze({ species: 'onix', level: 14 }),
    ]),
  }),
  Object.freeze({
    id: 'cascade', order: 2, city: 'Cerulean', type: 'água', leaderName: 'Misty',
    badgeId: 'cascade-badge', level: 21, recommendedPower: 260,
    team: Object.freeze([
      Object.freeze({ species: 'staryu', level: 19 }),
      Object.freeze({ species: 'starmie', level: 21 }),
    ]),
  }),
  Object.freeze({
    id: 'thunder', order: 3, city: 'Vermilion', type: 'elétrico', leaderName: 'Lt. Surge',
    badgeId: 'thunder-badge', level: 24, recommendedPower: 340,
    team: Object.freeze([
      Object.freeze({ species: 'voltorb', level: 21 }),
      Object.freeze({ species: 'pikachu', level: 18 }),
      Object.freeze({ species: 'raichu', level: 24 }),
    ]),
  }),
  Object.freeze({
    id: 'rainbow', order: 4, city: 'Celadon', type: 'planta', leaderName: 'Erika',
    badgeId: 'rainbow-badge', level: 29, recommendedPower: 430,
    team: Object.freeze([
      Object.freeze({ species: 'tangela', level: 24 }),
      Object.freeze({ species: 'victreebel', level: 29 }),
      Object.freeze({ species: 'vileplume', level: 29 }),
    ]),
  }),
  Object.freeze({
    id: 'soul', order: 5, city: 'Fuchsia', type: 'veneno', leaderName: 'Koga',
    badgeId: 'soul-badge', level: 37, recommendedPower: 560,
    team: Object.freeze([
      Object.freeze({ species: 'koffing', level: 37 }),
      Object.freeze({ species: 'muk', level: 39 }),
      Object.freeze({ species: 'weezing', level: 43 }),
    ]),
  }),
  Object.freeze({
    id: 'marsh', order: 6, city: 'Saffron', type: 'psíquico', leaderName: 'Sabrina',
    badgeId: 'marsh-badge', level: 43, recommendedPower: 680,
    team: Object.freeze([
      Object.freeze({ species: 'kadabra', level: 38 }),
      Object.freeze({ species: 'mr-mime', level: 37 }),
      Object.freeze({ species: 'alakazam', level: 43 }),
    ]),
  }),
  Object.freeze({
    id: 'volcano', order: 7, city: 'Cinnabar', type: 'fogo', leaderName: 'Blaine',
    badgeId: 'volcano-badge', level: 47, recommendedPower: 800,
    team: Object.freeze([
      Object.freeze({ species: 'growlithe', level: 42 }),
      Object.freeze({ species: 'ponyta', level: 40 }),
      Object.freeze({ species: 'rapidash', level: 42 }),
      Object.freeze({ species: 'arcanine', level: 47 }),
    ]),
  }),
  Object.freeze({
    id: 'earth', order: 8, city: 'Viridian', type: 'terra', leaderName: 'Giovanni',
    badgeId: 'earth-badge', level: 50, recommendedPower: 950,
    team: Object.freeze([
      Object.freeze({ species: 'nidoqueen', level: 44 }),
      Object.freeze({ species: 'nidoking', level: 45 }),
      Object.freeze({ species: 'dugtrio', level: 42 }),
      Object.freeze({ species: 'rhydon', level: 50 }),
    ]),
  }),
]);

export const GYM_BY_ID = Object.freeze(Object.fromEntries(GYMS.map((gym) => [gym.id, gym])));
export const MAX_GYM_REMATCHES_TRACKED = 20;

/** Escala de rematch pós-conquista: HP/status sobem, recompensa acompanha. */
export function rematchScaling(rematchCount = 0) {
  const count = clamp(Math.floor(Number(rematchCount) || 0), 0, MAX_GYM_REMATCHES_TRACKED);
  return Object.freeze({
    hpMult: Math.round((1 + count * 0.25) * 100) / 100,
    statMult: Math.round((1 + count * 0.15) * 100) / 100,
    rewardMult: Math.round((1 + count * 0.30) * 100) / 100,
  });
}

function rematchCountFor(state, gymId) {
  return Math.max(0, Math.floor(Number(state?.gymRematches?.[gymId]) || 0));
}

/** Um ginásio só é desafiável se o anterior na ordem já tiver sido vencido. */
export function canChallengeGym(state, gymId) {
  const gym = GYM_BY_ID[gymId];
  if (!gym) return false;
  if (gym.order <= 1) return true;
  const previous = GYMS.find((entry) => entry.order === gym.order - 1);
  const badges = Array.isArray(state?.badges) ? state.badges : [];
  return !previous || badges.includes(previous.badgeId);
}

/**
 * Stub de resolução: vitória se `teamPower >= recommendedPower` (ajustado por rematch).
 * Muta `state.badges`/`state.gymRematches` em caso de vitória. Retorno estável.
 */
export function resolveGymBattle(state, gymId, teamPower) {
  const gym = GYM_BY_ID[gymId];
  if (!state || typeof state !== 'object' || !gym) return { win: false, error: 'invalid_gym' };
  if (!canChallengeGym(state, gymId)) return { win: false, error: 'locked' };

  const rematchCount = rematchCountFor(state, gymId);
  const scale = rematchScaling(rematchCount);
  const required = Math.round(gym.recommendedPower * scale.statMult);
  const win = (Number(teamPower) || 0) >= required;

  if (win) {
    state.badges = Array.isArray(state.badges) ? state.badges : [];
    if (!state.badges.includes(gym.badgeId)) state.badges.push(gym.badgeId);
    state.gymRematches = state.gymRematches && typeof state.gymRematches === 'object' ? state.gymRematches : {};
    state.gymRematches[gymId] = rematchCount + 1;
  }

  return {
    win, required, rematchCount, rewardMult: scale.rewardMult, badgeId: gym.badgeId,
  };
}

export function hasAllBadges(state) {
  const badges = Array.isArray(state?.badges) ? state.badges : [];
  return GYMS.every((gym) => badges.includes(gym.badgeId));
}

/** Elite Four + Campeão — desbloqueados após as 8 insígnias. */
export const ELITE_FOUR = Object.freeze([
  Object.freeze({
    id: 'elite-lorelei', order: 1, type: 'gelo', leaderName: 'Lorelei',
    recommendedPower: 2200,
    team: Object.freeze([
      Object.freeze({ species: 'dewgong', level: 54 }),
      Object.freeze({ species: 'cloyster', level: 54 }),
      Object.freeze({ species: 'slowbro', level: 56 }),
      Object.freeze({ species: 'jynx', level: 56 }),
      Object.freeze({ species: 'lapras', level: 58 }),
    ]),
  }),
  Object.freeze({
    id: 'elite-bruno', order: 2, type: 'lutador', leaderName: 'Bruno',
    recommendedPower: 2400,
    team: Object.freeze([
      Object.freeze({ species: 'onix', level: 53 }),
      Object.freeze({ species: 'hitmonchan', level: 55 }),
      Object.freeze({ species: 'hitmonlee', level: 55 }),
      Object.freeze({ species: 'onix', level: 56 }),
      Object.freeze({ species: 'machamp', level: 58 }),
    ]),
  }),
  Object.freeze({
    id: 'elite-agatha', order: 3, type: 'fantasma', leaderName: 'Agatha',
    recommendedPower: 2600,
    team: Object.freeze([
      Object.freeze({ species: 'gengar', level: 56 }),
      Object.freeze({ species: 'golbat', level: 56 }),
      Object.freeze({ species: 'haunter', level: 55 }),
      Object.freeze({ species: 'arbok', level: 58 }),
      Object.freeze({ species: 'gengar', level: 60 }),
    ]),
  }),
  Object.freeze({
    id: 'elite-lance', order: 4, type: 'dragão', leaderName: 'Lance',
    recommendedPower: 2800,
    team: Object.freeze([
      Object.freeze({ species: 'gyarados', level: 58 }),
      Object.freeze({ species: 'dragonair', level: 56 }),
      Object.freeze({ species: 'dragonair', level: 56 }),
      Object.freeze({ species: 'aerodactyl', level: 60 }),
      Object.freeze({ species: 'dragonite', level: 62 }),
    ]),
  }),
]);

export const CHAMPION = Object.freeze({
  id: 'champion-blue', type: 'variado', leaderName: 'Blue',
  recommendedPower: 3200,
  team: Object.freeze([
    Object.freeze({ species: 'pidgeot', level: 61 }),
    Object.freeze({ species: 'alakazam', level: 59 }),
    Object.freeze({ species: 'rhydon', level: 61 }),
    Object.freeze({ species: 'arcanine', level: 61 }),
    Object.freeze({ species: 'exeggutor', level: 63 }),
    Object.freeze({ species: 'blastoise', level: 65 }),
  ]),
});

export function canChallengeElite(state, eliteId) {
  if (!hasAllBadges(state)) return false;
  const elite = ELITE_FOUR.find((entry) => entry.id === eliteId);
  if (!elite) return false;
  const progress = Array.isArray(state?.eliteProgress?.defeated) ? state.eliteProgress.defeated : [];
  if (elite.order <= 1) return true;
  const previous = ELITE_FOUR.find((entry) => entry.order === elite.order - 1);
  return !previous || progress.includes(previous.id);
}

export function resolveEliteBattle(state, eliteId, teamPower) {
  const elite = ELITE_FOUR.find((entry) => entry.id === eliteId);
  if (!state || !elite) return { win: false, error: 'invalid_elite' };
  if (!canChallengeElite(state, eliteId)) return { win: false, error: 'locked' };
  const win = (Number(teamPower) || 0) >= elite.recommendedPower;
  if (win) {
    state.eliteProgress = state.eliteProgress && typeof state.eliteProgress === 'object' ? state.eliteProgress : { defeated: [], championDefeated: false };
    state.eliteProgress.defeated = Array.isArray(state.eliteProgress.defeated) ? state.eliteProgress.defeated : [];
    if (!state.eliteProgress.defeated.includes(eliteId)) state.eliteProgress.defeated.push(eliteId);
  }
  return { win, required: elite.recommendedPower };
}

export function canChallengeChampion(state) {
  const defeated = Array.isArray(state?.eliteProgress?.defeated) ? state.eliteProgress.defeated : [];
  return hasAllBadges(state) && ELITE_FOUR.every((entry) => defeated.includes(entry.id));
}

export function resolveChampionBattle(state, teamPower) {
  if (!state) return { win: false, error: 'invalid_state' };
  if (!canChallengeChampion(state)) return { win: false, error: 'locked' };
  const win = (Number(teamPower) || 0) >= CHAMPION.recommendedPower;
  if (win) {
    state.eliteProgress = state.eliteProgress && typeof state.eliteProgress === 'object' ? state.eliteProgress : { defeated: [], championDefeated: false };
    state.eliteProgress.championDefeated = true;
  }
  return { win, required: CHAMPION.recommendedPower };
}
